import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import LandingPage from "@/pages/LandingPage";
import GetStartedPage from "@/pages/GetStartedPage";
import ChatPage from "@/pages/ChatPage";
import ProvidersPage from "@/pages/ProvidersPage";
import BrowseProvidersPage from "@/pages/BrowseProvidersPage";
import ProviderSignupPage from "@/pages/ProviderSignupPage";
import ProviderInquiryPage from "@/pages/ProviderInquiryPage";
import HistoryPage from "@/pages/HistoryPage";
import PrintSummaryPage from "@/pages/PrintSummaryPage";
import LibraryPage from "@/pages/LibraryPage";
import ArticlePage from "@/pages/ArticlePage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import PortalPage from "@/pages/PortalPage";
import FormPrintPage from "@/pages/FormPrintPage";
import ConsultRoomPage from "@/pages/ConsultRoomPage";
import ProviderJoinPage from "@/pages/ProviderJoinPage";
import ProviderDashboardPage from "@/pages/ProviderDashboardPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import MusicPage from "@/pages/MusicPage";
import BooksPage from "@/pages/BooksPage";
import ShoppingPage from "@/pages/ShoppingPage";
import ShoppingComingSoonPage from "@/pages/ShoppingComingSoonPage";
import InspirationComingSoonPage from "@/pages/InspirationComingSoonPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsPage from "@/pages/TermsPage";
import HipaaNoticePage from "@/pages/HipaaNoticePage";
import TierPolicyPage from "@/pages/TierPolicyPage";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import LegalConsentModal from "@/components/LegalConsentModal";

function AppRouter() {
  const location = useLocation();
  // Google OAuth callback: session_id arrives in the URL fragment - process it FIRST
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/get-started" element={<GetStartedPage />} />
      <Route path="/chat" element={<ChatPage />} />
      <Route path="/chat/:sessionId" element={<ChatPage />} />
      <Route path="/providers" element={<ProvidersPage />} />
      <Route path="/browse-providers" element={<BrowseProvidersPage />} />
      <Route path="/provider-signup" element={<ProviderInquiryPage />} />
      {/* Invite-only tier onboarding — not linked publicly so families don't
          accidentally land on subscription pricing. Reach it via a direct
          link from an admin invite email once a provider inquiry is approved. */}
      <Route path="/provider-onboarding" element={<ProviderSignupPage />} />
      <Route path="/history" element={<HistoryPage />} />
      <Route path="/print/:sessionId" element={<PrintSummaryPage />} />
      <Route path="/library" element={<LibraryPage />} />
      <Route path="/article/:articleId" element={<ArticlePage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/portal" element={<PortalPage />} />
      <Route path="/portal/forms/:formId/print" element={<FormPrintPage />} />
      <Route path="/consult/join/:joinCode" element={<ProviderJoinPage />} />
      <Route path="/consult/:consultId" element={<ConsultRoomPage />} />
      <Route path="/provider-dashboard" element={<ProviderDashboardPage />} />
      <Route path="/admin" element={<AdminDashboardPage />} />
      <Route path="/music" element={<MusicPage />} />
      <Route path="/books" element={<BooksPage />} />
      {/* /shopping is intentionally routed to the Coming Soon page for now.
          The full affiliate storefront (ShoppingPage) remains in the repo — to
          re-open access, swap the two route lines below. */}
      <Route path="/shopping" element={<ShoppingComingSoonPage />} />
      <Route path="/shopping-preview" element={<ShoppingPage />} />
      <Route path="/inspiration" element={<InspirationComingSoonPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/legal/terms" element={<TermsPage />} />
      <Route path="/legal/hipaa-notice" element={<HipaaNoticePage />} />
      <Route path="/tier-policy" element={<TierPolicyPage />} />
    </Routes>
  );
}

function GlobalLegalConsent() {
  const { user } = useAuth();
  // Only prompt families — providers and admins have separate flows.
  if (!user || user.role !== "family") return null;
  return <LegalConsentModal user={user} />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <GlobalLegalConsent />
          <PWAInstallPrompt />
        </BrowserRouter>
      </AuthProvider>
      <Toaster position="top-right" />
    </div>
  );
}

export default App;
